// Microsoft Azure AD SSO integration
import {
    PublicClientApplication,
    Configuration,
    AccountInfo,
    AuthenticationResult,
    SilentRequest,
    RedirectRequest,
    EndSessionRequest
} from '@azure/msal-browser';

class AuthService {
    private msalInstance: PublicClientApplication;
    private initializePromise: Promise<void> | null = null;
    private readonly tenantId = 'f92cfe88-1fd0-4353-afc2-21c423f63911';
    private readonly clientId = 'b05335e6-32c9-44bc-95fa-6a2a566657a6';
    private readonly adminGroup = 'ADMIN';
    private defaultScopes: Array<string> = ['api://parkit.prod/b05335e6-32c9-44bc-95fa-6a2a566657a6/access_as_user'];

    constructor() {
        const msalConfig: Configuration = {
            auth: {
                clientId: this.clientId,
                authority: `https://login.microsoftonline.com/${this.tenantId}`,
                redirectUri: window.location.origin,
                postLogoutRedirectUri: window.location.origin
            },
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: false
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level, message, containsPii) => {
                        if (containsPii) return;
                        console.log(message);
                    },
                    logLevel: 1
                }
            }
        };

        this.msalInstance = new PublicClientApplication(msalConfig);
    }

    public async initialize(): Promise<void> {
        if (!this.initializePromise) {
            this.initializePromise = this.msalInstance.initialize()
                .then(() => this.msalInstance.handleRedirectPromise())
                .then(() => undefined);
        }
        await this.initializePromise;
    }

    public async login(): Promise<void> {
        await this.initialize();
        const loginRequest: RedirectRequest = {
            scopes: this.defaultScopes,
            prompt: 'select_account'
        };

        try {
            await this.msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    }

    public async logout(): Promise<void> {
        await this.initialize();
        const account = this.getActiveAccount();
        if (account) {
            const logoutRequest: EndSessionRequest = {
                account: account,
                postLogoutRedirectUri: window.location.origin
            };
            await this.msalInstance.logoutRedirect(logoutRequest);
        }
    }

    public getActiveAccount(): AccountInfo | null {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length === 0) {
            return null;
        }
        return accounts[0];
    }

    public async getUser(): Promise<AccountInfo | null> {
        await this.initialize();
        return this.getActiveAccount();
    }

    public async getAccessToken(): Promise<string | null> {
        await this.initialize();
        const currentAccount = this.getActiveAccount();
        if (!currentAccount) return null;

        try {
            const response: AuthenticationResult = await this.msalInstance.acquireTokenSilent({
                scopes: this.defaultScopes,
                account: currentAccount
            });
            return response.accessToken;
        } catch (error) {
            console.error('Token acquisition failed:', error);
            return null;
        }
    }
}

export default new AuthService();